import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { Eye, X, ExternalLink, CheckCircle, Trash2 } from 'lucide-react';

export default function Emails() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    loadEmails();
  }, [filter]);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const params = filter === 'unanalyzed' ? { onlyUnanalyzed: true } : {};
      const data = await api.getEmails(params);
      setEmails(data.emails);
    } catch (error) {
      console.error('Failed to load emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!confirm('Opravdu chcete smazat tento email?')) return;

    try {
      await api.deleteEmail(emailId);
      // Reload emails after deletion
      await loadEmails();
    } catch (error) {
      console.error('Failed to delete email:', error);
      alert('Chyba při mazání emailu: ' + error.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Emaily</h1>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Všechny
          </button>
          <button
            onClick={() => setFilter('unanalyzed')}
            className={`btn ${filter === 'unanalyzed' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Neanalyzované
          </button>
        </div>
      </div>

      {loading ? (
        <div>Načítání...</div>
      ) : (
        <div className="space-y-4">
          {emails.map(email => (
            <EmailCard
              key={email.id}
              email={email}
              onView={() => setSelectedEmail(email)}
              onDelete={() => handleDeleteEmail(email.id)}
            />
          ))}

          {emails.length === 0 && (
            <div className="card text-center py-12">
              <p className="text-gray-600">Žádné emaily nenalezeny</p>
            </div>
          )}
        </div>
      )}

      {selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  );
}

function EmailCard({ email, onView, onDelete }) {
  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 cursor-pointer" onClick={onView}>
          <div className="flex items-center space-x-2 mb-1">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: email.metadata?.color || '#9E9E9E' }}
            />
            <span className="text-sm font-medium text-gray-600">
              {email.sourceName}
            </span>
          </div>
          <h3 className="font-semibold text-lg text-gray-900">{email.subject}</h3>
        </div>

        <div className="flex items-center space-x-2">
          <span className={`badge ${email.analyzed ? 'badge-success' : 'badge-warning'}`}>
            {email.analyzed ? 'Analyzováno' : 'Čeká'}
          </span>
          <span className="text-sm text-gray-500">
            {format(new Date(email.receivedAt), 'd. M. yyyy HH:mm', { locale: cs })}
          </span>
          <button
            onClick={onView}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            title="Zobrazit email"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-gray-400 hover:text-red-600 transition-colors p-1"
            title="Smazat email"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <p className="text-gray-700 text-sm line-clamp-2">
        {email.bodyText.substring(0, 200)}...
      </p>

      <div className="flex items-center justify-between mt-4 pt-4 border-t">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">Od: {email.metadata.from}</span>
        </div>

        <div className="flex flex-wrap gap-1">
          {email.metadata.tags?.map(tag => (
            <span key={tag} className="badge badge-info text-xs">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmailDetailModal({ email, onClose }) {
  const [activeTab, setActiveTab] = useState('html');
  const [links, setLinks] = useState([]);

  useEffect(() => {
    // Extract all links from email body
    const urlRegex = /(https?:\/\/[^\s<>"]+)/g;
    const foundLinks = [];

    // Extract from text
    const textMatches = email.bodyText.match(urlRegex) || [];
    foundLinks.push(...textMatches);

    // Extract from HTML
    if (email.bodyHtml) {
      const htmlMatches = email.bodyHtml.match(urlRegex) || [];
      foundLinks.push(...htmlMatches);
    }

    // Deduplicate and filter
    const uniqueLinks = [...new Set(foundLinks)];
    setLinks(uniqueLinks);
  }, [email]);

  const confirmationKeywords = ['confirm', 'verify', 'activate', 'subscribe', 'unsubscribe', 'opt-in', 'click here'];

  const isConfirmationLink = (url) => {
    const lowerUrl = url.toLowerCase();
    return confirmationKeywords.some(keyword => lowerUrl.includes(keyword));
  };

  const confirmationLinks = links.filter(isConfirmationLink);
  const otherLinks = links.filter(url => !isConfirmationLink(url));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b">
          <div className="flex-1 pr-4">
            <div className="flex items-center space-x-2 mb-2">
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: email.metadata?.color || '#9E9E9E' }}
              />
              <span className="text-sm font-medium text-gray-600">
                {email.sourceName}
              </span>
              <span className={`badge ${email.analyzed ? 'badge-success' : 'badge-warning'}`}>
                {email.analyzed ? 'Analyzováno' : 'Čeká'}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{email.subject}</h2>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>Od: {email.metadata.from}</span>
              <span>•</span>
              <span>{format(new Date(email.receivedAt), 'd. MMMM yyyy HH:mm', { locale: cs })}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Confirmation Links Alert */}
        {confirmationLinks.length > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex items-start">
              <CheckCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2">
                  Potvrzovací linky ({confirmationLinks.length})
                </h3>
                <div className="space-y-2">
                  {confirmationLinks.slice(0, 3).map((link, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-yellow-700 hover:text-yellow-900 underline break-all flex-1"
                      >
                        {link.substring(0, 80)}...
                      </a>
                      <ExternalLink size={14} className="text-yellow-600 flex-shrink-0" />
                    </div>
                  ))}
                  {confirmationLinks.length > 3 && (
                    <p className="text-xs text-yellow-700">
                      +{confirmationLinks.length - 3} dalších odkazů v záložce "Odkazy"
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b">
          <div className="flex space-x-1 px-6">
            <button
              onClick={() => setActiveTab('text')}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'text'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Text
            </button>
            <button
              onClick={() => setActiveTab('html')}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'html'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              HTML
            </button>
            <button
              onClick={() => setActiveTab('links')}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'links'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Odkazy ({links.length})
            </button>
            <button
              onClick={() => setActiveTab('raw')}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'raw'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Raw JSON
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'text' && (
            <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
              {email.bodyText}
            </pre>
          )}

          {activeTab === 'html' && (
            <div>
              <div className="mb-4 p-3 bg-gray-100 rounded">
                <p className="text-sm text-gray-600">HTML Preview:</p>
              </div>
              <iframe
                srcDoc={`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <meta charset="UTF-8">
                      <style>
                        body {
                          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                          line-height: 1.6;
                          color: #000;
                          background: #fff;
                          margin: 0;
                          padding: 20px;
                        }
                        a { color: #0066cc; }
                        img { max-width: 100%; height: auto; }
                      </style>
                    </head>
                    <body>${email.bodyHtml}</body>
                  </html>
                `}
                className="w-full border border-gray-200 rounded"
                style={{ minHeight: '400px', height: '70vh' }}
                sandbox="allow-same-origin"
                title="Email HTML Preview"
              />
            </div>
          )}

          {activeTab === 'links' && (
            <div className="space-y-4">
              {confirmationLinks.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle size={18} className="text-yellow-600 mr-2" />
                    Potvrzovací odkazy ({confirmationLinks.length})
                  </h3>
                  <div className="space-y-2">
                    {confirmationLinks.map((link, index) => (
                      <div key={index} className="flex items-center space-x-2 p-3 bg-yellow-50 rounded hover:bg-yellow-100 transition-colors">
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-yellow-700 hover:text-yellow-900 underline break-all flex-1"
                        >
                          {link}
                        </a>
                        <ExternalLink size={16} className="text-yellow-600 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {otherLinks.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Ostatní odkazy ({otherLinks.length})
                  </h3>
                  <div className="space-y-1">
                    {otherLinks.map((link, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded transition-colors">
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 underline break-all flex-1"
                        >
                          {link}
                        </a>
                        <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {links.length === 0 && (
                <p className="text-gray-600 text-center py-8">Žádné odkazy nenalezeny</p>
              )}
            </div>
          )}

          {activeTab === 'raw' && (
            <pre className="whitespace-pre-wrap font-mono text-xs text-gray-800 bg-gray-50 p-4 rounded">
              {JSON.stringify(email, null, 2)}
            </pre>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
